import { useState, useRef, useEffect } from 'react'
import { Link, NavLink, useNavigate, useLocation } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGauge, faFileLines, faFolder, faRotate, faChartBar, faPlug, faLink,
  faServer, faWandMagicSparkles, faFlask, faDatabase,
  faGear, faClipboardList, faCreditCard, faShield, faBook, faCode,
  faRightFromBracket, faBars, faXmark, faChevronDown, faChevronRight,
  faKey, faUsers, faLayerGroup, faCubes, faUserShield, faUserCheck, faFileSignature, faStamp,
} from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useAuth } from '../contexts/AuthContext'
import { useAppState, useWorkspaceTheme } from '../contexts/AppStateContext'
import { BreadcrumbProvider, useBreadcrumb, type Crumb } from '../contexts/BreadcrumbContext'
import { useIsMobile } from '../hooks/useMediaQuery'
import { Avatar } from './ui/Avatar'
import { GlobalSearch } from './GlobalSearch'
import { NotificationCenter } from './NotificationCenter'
import { MfaDeadlineBanner } from './MfaDeadlineBanner'
import { ClosedTeamGate, TeamClosedBanner } from './TeamClosedBanner'
import { SecurityRole } from '../constants'
import { colors, fontFamily, fontSize, radius, shadow, layout, transition, eyebrow as eyebrowStyle } from '../styles/theme'

const SW = layout.sidebarWidth
const TH = layout.topbarHeight

// ── Nav structure ─────────────────────────────────────────────────────────
interface NavItem {
  to: string
  label: string
  icon: IconDefinition
  /**
   * Who may see this item. 'teamAdmin' is team-scoped (the Team page); 'superAdmin' is the
   * site-wide admin area. Previously a single `adminOnly` flag covered both, so a team admin saw
   * links into pages listing every tenant in the system.
   */
  requires?: 'teamAdmin' | 'superAdmin'
}

const PRIMARY_GROUPS: { label?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: '/app/dashboard', label: 'Dashboard', icon: faGauge },
      { to: '/app/templates', label: 'Templates',  icon: faFileLines },
      { to: '/app/blocks',    label: 'Blocks',     icon: faCubes },
      { to: '/app/approvals', label: 'Approvals',  icon: faStamp },
    ],
  },
  {
    label: 'Manage',
    items: [
      { to: '/app/api-keys',     label: 'API keys',      icon: faKey },
      // Every member: it is where they leave the team (admins also manage it there).
      { to: '/app/team',        label: 'Team',          icon: faUsers },
      { to: '/app/roles-permissions', label: 'Roles & Permissions', icon: faUserShield, requires: 'teamAdmin' },
      { to: '/app/access-review', label: 'Access Review', icon: faUserCheck, requires: 'teamAdmin' },
      { to: '/app/analytics',    label: 'Analytics',     icon: faChartBar },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/app/billing',  label: 'Billing',  icon: faCreditCard },
      { to: '/app/settings', label: 'Settings', icon: faGear },
    ],
  },
]

const MORE_ITEMS: NavItem[] = [
  { to: '/app/render-jobs',  label: 'Render Jobs',   icon: faRotate },
  { to: '/app/esign',        label: 'E-signatures',  icon: faFileSignature },
  { to: '/app/connectors',   label: 'Connectors',    icon: faPlug },
  { to: '/app/integrations', label: 'Integrations',  icon: faLink },
  { to: '/app/ai',           label: 'AI Assistant',  icon: faWandMagicSparkles },
  { to: '/app/playground',   label: 'Playground',    icon: faFlask },
  { to: '/app/warehouse',    label: 'Warehouse',     icon: faDatabase },
  { to: '/app/audit-log',    label: 'Audit Log',     icon: faClipboardList },
  { to: '/app/admin',        label: 'Admin',         icon: faShield, requires: 'superAdmin' },
  { to: '/app/admin/users',  label: 'Users',         icon: faUsers,  requires: 'superAdmin' },
  { to: '/app/admin/logs',   label: 'Logs',          icon: faClipboardList, requires: 'superAdmin' },
]

const DOC_ITEMS: NavItem[] = [
  { to: '/docs/templates', label: 'Template Guide', icon: faBook },
  { to: '/docs/api',       label: 'API Reference',  icon: faCode },
]

// ── NavItem component ─────────────────────────────────────────────────────
function NavItemRow({ item, collapsed, onClick }: { item: NavItem; collapsed?: boolean; onClick?: () => void }) {
  const [hovered, setHovered] = useState(false)
  return (
    <NavLink
      to={item.to}
      end={item.to === '/app/dashboard'}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: collapsed ? '9px 0' : '8px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        margin: collapsed ? '1px 6px' : '1px 8px',
        borderRadius: radius.md,
        color: isActive ? colors.sidebarActive : hovered ? colors.sidebarTextHover : colors.sidebarText,
        background: isActive ? colors.sidebarActiveBg : hovered && !isActive ? colors.sidebarHover : 'transparent',
        textDecoration: 'none',
        fontSize: fontSize.sm,
        fontWeight: isActive ? 500 : 400,
        fontFamily: fontFamily.sans,
        letterSpacing: '-0.01em',
        transition: `background ${transition.fast}, color ${transition.fast}`,
        boxSizing: 'border-box',
      })}
    >
      <FontAwesomeIcon icon={item.icon} style={{ width: 14, flexShrink: 0 }} />
      {!collapsed && <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>}
    </NavLink>
  )
}

// ── Bottom user menu ──────────────────────────────────────────────────────
function SidebarUserMenu({ collapsed }: { collapsed: boolean }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || ''
  const roleLabel = (user?.securityRole ?? 0) >= SecurityRole.SUPER_ADMIN ? 'Super Admin' : (user?.securityRole ?? 0) >= SecurityRole.TEAM_ADMIN ? 'Team Admin' : (user?.securityRole ?? 0) >= SecurityRole.EDITOR ? 'Editor' : 'Viewer'

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  return (
    <div ref={ref} style={{ padding: collapsed ? '10px 6px' : '10px 14px', borderTop: `1px solid ${colors.border}`, position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center',
          gap: collapsed ? 0 : 9,
          justifyContent: collapsed ? 'center' : 'flex-start',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <Avatar name={displayName} size={28} />
        {!collapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
              <div style={{ fontFamily: fontFamily.mono, fontSize: '10px', color: colors.textMuted }}>
                {roleLabel}
              </div>
            </div>
            <FontAwesomeIcon icon={faChevronDown} style={{ width: 10, color: colors.textMuted, flexShrink: 0 }} />
          </>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          left: 10, right: 10,
          bottom: 'calc(100% + 6px)',
          zIndex: 200,
          background: colors.surface,
          borderRadius: radius.md,
          border: `1px solid ${colors.border}`,
          boxShadow: shadow.lg,
          padding: '4px 0',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '11px 14px', borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text }}>{displayName}</div>
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 2 }}>{user?.email}</div>
          </div>
          <PopMenuItem label="Profile" onClick={() => { navigate('/app/settings'); setOpen(false) }} />
          <PopMenuItem label="Sign out" onClick={() => { handleLogout(); setOpen(false) }} icon={faRightFromBracket} danger />
        </div>
      )}
    </div>
  )
}

function PopMenuItem({ label, onClick, icon, danger }: { label: string; onClick: () => void; icon?: IconDefinition; danger?: boolean }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '9px 14px', border: 'none',
        background: hovered ? colors.surfaceTinted ?? '#F4FAF9' : 'transparent',
        color: danger ? colors.error : colors.text,
        fontSize: fontSize.sm, fontFamily: fontFamily.sans,
        cursor: 'pointer', textAlign: 'left', letterSpacing: '-0.01em',
      }}
    >
      {icon && <FontAwesomeIcon icon={icon} style={{ width: 13 }} />}
      {label}
    </button>
  )
}

// ── TopBar ────────────────────────────────────────────────────────────────
function TopBar() {

  return (
    <div style={{
      height: TH,
      background: colors.surface,
      borderBottom: `1px solid ${colors.border}`,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      padding: '0 24px',
      flexShrink: 0,
    }}>
      {/* Global search */}
      <GlobalSearch />

      {/* Right side: notification bell */}
      <div style={{ marginLeft: 'auto' }} />
      <NotificationCenter />
    </div>
  )
}

// ── Sidebar content ────────────────────────────────────────────────────────
function SidebarContent({ collapsed, onNav }: { collapsed: boolean; onNav?: () => void }) {
  const { user } = useAuth()
  const role = user?.securityRole ?? 0
  const canSee = (item: NavItem) =>
    item.requires === 'superAdmin' ? role >= SecurityRole.SUPER_ADMIN
    : item.requires === 'teamAdmin' ? role >= SecurityRole.TEAM_ADMIN
    : true
  const [moreOpen, setMoreOpen] = useState(false)

  return (
    <>
      {/* Brand lockup */}
      <div style={{
        padding: collapsed ? '14px 0' : '14px 16px',
        display: 'flex', alignItems: 'center',
        gap: 8,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderBottom: `1px solid ${colors.border}`,
        flexShrink: 0,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: colors.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <DocIcon />
        </div>
        {!collapsed && (
          <span style={{
            fontSize: '15px', fontWeight: 600, color: colors.text,
            letterSpacing: '-0.02em', fontFamily: fontFamily.sans,
          }}>
            Doc<span style={{ color: colors.primary }}>morph</span>ia
          </span>
        )}
      </div>

      {/* Workspace switcher (Live/Test) */}
      <WorkspaceSwitcher collapsed={collapsed} />

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {PRIMARY_GROUPS.map((group, gi) => (
          <div key={gi} style={{ marginBottom: 4 }}>
            {group.label && !collapsed && (
              <div style={{
                ...eyebrowStyle,
                padding: '6px 20px 2px',
                fontSize: '10px',
              }}>
                {group.label}
              </div>
            )}
            {group.items
              .filter(canSee)
              .map(item => (
                <NavItemRow key={item.to} item={item} collapsed={collapsed} onClick={onNav} />
              ))}
          </div>
        ))}

        {/* More group */}
        <div style={{ marginTop: 4 }}>
          {!collapsed && (
            <button
              onClick={() => setMoreOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 20px', width: '100%',
                background: 'none', border: 'none',
                cursor: 'pointer',
                ...eyebrowStyle,
                fontSize: '10px',
              }}
            >
              <FontAwesomeIcon icon={moreOpen ? faChevronDown : faChevronRight} style={{ width: 8 }} />
              More
            </button>
          )}
          {(moreOpen || collapsed) && MORE_ITEMS
            .filter(canSee)
            .map(item => (
              <NavItemRow key={item.to} item={item} collapsed={collapsed} onClick={onNav} />
            ))}
        </div>

        {/* Docs */}
        {!collapsed && (
          <div style={{ marginTop: 8, borderTop: `1px solid ${colors.border}`, paddingTop: 8 }}>
            <div style={{ ...eyebrowStyle, padding: '4px 20px 2px', fontSize: '10px' }}>Docs</div>
            {DOC_ITEMS.map(item => (
              <NavItemRow key={item.to} item={item} collapsed={collapsed} onClick={onNav} />
            ))}
          </div>
        )}
      </nav>

      <SidebarUserMenu collapsed={collapsed} />
    </>
  )
}

// ── Workspace switcher (Live/Test) ───────────────────────────────────────────
function WorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const { workspaces, currentWorkspace, setCurrentWorkspace } = useAppState()
  if (workspaces.length === 0) return null

  const sorted = [...workspaces].sort((a, b) => (a.isTest ? 1 : -1))

  return (
    <div style={{
      padding: collapsed ? '10px 8px' : '10px 16px',
      borderBottom: `1px solid ${colors.border}`,
      flexShrink: 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        background: colors.background, borderRadius: radius.full,
        border: `1px solid ${colors.border}`, padding: 2, gap: 2,
        justifyContent: collapsed ? 'center' : 'stretch',
      }}>
        {sorted.map(workspace => {
          const active = workspace.workspaceId === currentWorkspace?.workspaceId
          return (
            <button
              key={workspace.workspaceId}
              onClick={() => setCurrentWorkspace(workspace)}
              title={workspace.isTest ? 'Test' : 'Live'}
              style={{
                height: 24, padding: collapsed ? '0 6px' : '0 10px',
                flex: collapsed ? 'none' : 1,
                borderRadius: radius.full, border: 'none',
                // No isTest branch needed: the selected pill is only ever the current workspace,
                // and selecting Test puts data-theme="test" on <html>, which retints
                // --color-primary to #C2410C. So Test fills orange/red and Live fills teal from
                // the same rule, and the pill always matches the rest of the retinted UI.
                background: active ? colors.primary : 'transparent',
                color: active ? colors.onPrimary : colors.textMuted,
                fontSize: '11px', fontFamily: fontFamily.sans,
                fontWeight: active ? 600 : 400, cursor: 'pointer',
                transition: `background ${transition.fast}, color ${transition.fast}`,
                whiteSpace: 'nowrap',
              }}
            >
              {collapsed ? (workspace.isTest ? 'T' : 'L') : (workspace.isTest ? 'Test' : 'Live')}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Brand icon ─────────────────────────────────────────────────────────────
function DocIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 20 20" fill="none">
      <path d="M4 3h8l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="#fff" opacity={0.9} />
      <path d="M12 3l4 4h-4V3z" fill="#fff" opacity={0.6} />
      <path d="M7 9h6M7 12h6M7 15h4" style={{ stroke: colors.primary }} strokeWidth={1.2} strokeLinecap="round" />
    </svg>
  )
}

// ── Breadcrumb ─────────────────────────────────────────────────────────────
function Breadcrumb() {
  const location = useLocation()
  const { trail } = useBreadcrumb()

  // Route-derived default. A page that knows its real position (e.g. a template inside a folder
  // chain) publishes a trail via usePublishBreadcrumb and that wins.
  const parts = location.pathname.replace(/^\/app\/?/, '').split('/').filter(Boolean)
  const routeCrumbs: Crumb[] = parts.map((p, i) => ({
    label: p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' '),
    to: `/app/${parts.slice(0, i + 1).join('/')}`,
  }))

  const crumbs = trail ?? routeCrumbs

  return (
    <div style={{
      height: 38,
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '0 16px',
      borderBottom: `1px solid ${colors.border}`,
      background: colors.surface,
      flexShrink: 0,
    }}>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1
        const style = {
          fontSize: fontSize.sm,
          color: isLast ? colors.text : colors.textSecondary,
          fontWeight: isLast ? 500 : 400,
          letterSpacing: '-0.01em',
        }
        // The leaf is where you already are, so it never links even when it carries a `to`.
        const linkTo = isLast ? undefined : crumb.to
        return (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {linkTo
              ? <Link to={linkTo} style={{ ...style, textDecoration: 'none' }}>{crumb.label}</Link>
              : <span style={style}>{crumb.label}</span>}
            {!isLast && <Chevron />}
          </span>
        )
      })}
    </div>
  )
}

function Chevron() {
  return (
    <svg width={10} height={10} viewBox="0 0 10 10" fill="none">
      <path d="M3 2l4 3-4 3" stroke={colors.textMuted} strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ── Main export ────────────────────────────────────────────────────────────
/**
 * The provider has to sit above both the Breadcrumb and the Outlet, and both layout branches
 * render their own pair, so it wraps the whole shell rather than living in App.tsx.
 */
export default function AppLayout() {
  return (
    <BreadcrumbProvider>
      <AppLayoutShell />
    </BreadcrumbProvider>
  )
}

function AppLayoutShell() {
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const { currentWorkspace } = useAppState()
  useWorkspaceTheme()
  // Remounts every routed page on workspace switch so each re-runs its mount-effect fetch
  // against the newly-selected workspace, without touching every page individually.
  const outletKey = currentWorkspace?.workspaceId ?? 'none'

  const sidebarWidth = collapsed ? 56 : SW

  if (isMobile) {
    return (
      <div className="app-shell" style={{ display: 'flex', flexDirection: 'column', background: colors.background, overflow: 'hidden' }}>
        {/* Mobile topbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: `0 16px`, height: TH,
          background: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: colors.textSecondary, display: 'flex', alignItems: 'center' }}
          >
            <FontAwesomeIcon icon={faBars} style={{ width: 16 }} />
          </button>
          <span style={{ fontSize: '15px', fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', fontFamily: fontFamily.sans }}>
            Doc<span style={{ color: colors.primary }}>morph</span>ia
          </span>
          <div style={{ flex: 1 }} />
          <GlobalSearch />
        </div>

        {/* Drawer overlay */}
        {drawerOpen && (
          <div onClick={() => setDrawerOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(10,20,19,0.25)', zIndex: 100 }} />
        )}

        {/* Drawer */}
        <aside style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: SW,
          background: colors.surface,
          borderRight: `1px solid ${colors.border}`,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', zIndex: 101,
          transform: drawerOpen ? 'translateX(0)' : `translateX(-${SW}px)`,
          transition: `transform ${transition.slow}`,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: `1px solid ${colors.border}`,
          }}>
            <span style={{ fontSize: '15px', fontWeight: 600, color: colors.text, letterSpacing: '-0.02em' }}>
              Doc<span style={{ color: colors.primary }}>morph</span>ia
            </span>
            <button
              onClick={() => setDrawerOpen(false)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: colors.textSecondary }}
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faXmark} style={{ width: 14 }} />
            </button>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SidebarContent collapsed={false} onNav={() => setDrawerOpen(false)} />
          </div>
        </aside>

        <TeamClosedBanner />
        <Breadcrumb />
        {/* Phones/tablets: content never widens the page; wide pieces (tab strips, tables) scroll themselves. */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, padding: 16 }}>
          <ClosedTeamGate outletKey={outletKey} />
        </main>
      </div>
    )
  }

  return (
    <div className="app-shell" style={{ display: 'flex', overflow: 'hidden', background: colors.background }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        background: colors.surface,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: `width ${transition.slow}, min-width ${transition.slow}`,
        flexShrink: 0,
      }}>
        <SidebarContent collapsed={collapsed} />
      </aside>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(c => !c)}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{
          position: 'absolute',
          top: 20,
          left: sidebarWidth - 10,
          width: 20, height: 20,
          borderRadius: '50%',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10,
          boxShadow: shadow.sm,
          transition: `left ${transition.slow}`,
          color: colors.textMuted,
        }}
      >
        <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronDown}
          style={{ width: 8, transform: collapsed ? 'rotate(0deg)' : 'rotate(-90deg) translateX(1px)' }} />
      </button>

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <TopBar />
        <MfaDeadlineBanner />
        <TeamClosedBanner />
        <Breadcrumb />
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, padding: layout.contentPadding, background: colors.background }}>
          <ClosedTeamGate outletKey={outletKey} />
        </main>
      </div>
    </div>
  )
}
